/**
 * B469 — the tenant-feature ops behind the adoption face (docs/
 * TENANT-CUSTOMIZATION.md): the tenant admin lists what is pending or on
 * ("What's new — turn on when your team is ready", operator words straight
 * from the registry) and adopts on their own clock. Permissions ride the
 * ops-config precedent exactly (a dedicated scope would be an authorizer
 * amendment, deliberately not taken): the read on ledger:read, the ADMIN
 * write on channel:write.
 */
import { z } from "zod";
import { readOp, writeOp, zEpochMs } from "./common.js";

const zFeatureRow = z
  .object({
    key: z.string().min(1).max(120),
    words: z.string().min(1),
    owner: z.string().min(1),
    introducedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    decideBy: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    enabled: z.boolean(),
    /** How it got this way; "default" = no row, the ship-dark default. */
    source: z.enum(["adopted", "born-on", "ruled", "reverted", "default"]),
    at: zEpochMs.optional(),
  })
  .strict();

export const tenantFeaturesList = readOp({
  name: "tenant.features.list",
  permission: "ledger:read",
  principals: ["STAFF:ADMIN"],
  fields: {},
  response: z.object({ features: z.array(zFeatureRow).max(200) }).strict(),
  exampleRequest: {},
  exampleResponse: {
    features: [
      {
        key: "feature.orders.example",
        words: "An example feature, described the way an operator would say it.",
        owner: "pathik",
        introducedOn: "2026-09-15",
        decideBy: "2026-12-15",
        enabled: false,
        source: "default",
      },
    ],
  },
});

export const tenantFeaturesAdopt = writeOp({
  name: "tenant.features.adopt",
  permission: "channel:write",
  principals: ["STAFF:ADMIN"],
  fields: { key: z.string().min(1).max(120) },
  response: z.object({ key: z.string().min(1).max(120), enabled: z.literal(true), at: zEpochMs }).strict(),
  exampleRequest: { key: "feature.orders.example" },
  exampleResponse: { key: "feature.orders.example", enabled: true, at: 1_780_000_000_000 },
});

/**
 * B644 — turn an adopted feature back OFF. The admin who turned it on before
 * the team was ready has a door back, while the old world is still maintained
 * (before the registry's decideBy). After that day the platform refuses with
 * the why — the flag is resolved and the feature stays on.
 */
export const tenantFeaturesRevert = writeOp({
  name: "tenant.features.revert",
  permission: "channel:write",
  principals: ["STAFF:ADMIN"],
  fields: { key: z.string().min(1).max(120) },
  response: z.object({ key: z.string().min(1).max(120), enabled: z.literal(false), at: zEpochMs }).strict(),
  exampleRequest: { key: "feature.orders.example" },
  exampleResponse: { key: "feature.orders.example", enabled: false, at: 1_780_000_000_000 },
});
